import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { difference, noop, startCase, toLower, values } from 'lodash'
import { Link } from 'react-router-dom'
import Dinero from 'dinero.js'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { Stack } from '@mui/material'

import { useGetInboundTicketsQuery } from 'services/inboundTickets'

import DataGridWithSearchV2 from 'components/generic/datagrid/DataGridWithSearchV2'
import Chip from 'components/generic/chip/Chip'

import { buildFilter, setDecimalPrecision } from 'utils/helpers'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import useRenderDateRangePicker from 'utils/custom-hooks/useRenderDateRangePicker'
import usePaymentTerms from 'utils/custom-hooks/usePaymentTerms'
import {
  ALL_PAYMENT_METHOD_OPTIONS,
  CURRENCY_PREPENDS,
  FILTER_OPTION_TYPES,
  PAGE_SIZE,
  SORTABLE_IBT_FIELDS,
  TICKET_STATUSES,
} from 'utils/constants'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import DialogModal from '.'
import Big from 'big.js'

const InboundTicketsModal = ({
  onAdd,
  onClose,
  selectedList,
  billIds,
  currentCustomer,
  getPaymentTerms,
  currentCurrency,
  isBatchBill,
  ...restProps
}) => {
  const apiRef = useGridApiRef()

  const { renderDateRangePicker, startDate, endDate } = useRenderDateRangePicker()
  const { getDueDateFormatted, getPaymentTermNameById, paymentTermsOptions } = usePaymentTerms({})

  const { currencyExchangeRatesFF, enableLockAccountingV2 } = useAppSelector(
    state => state.featureFlags?.variables || {},
  )
  const { defaultYardTimezone } = useAppSelector(state => state.auth)
  const { defaultYardUm, settings, defaultCurrency } = useAppSelector(state => state.settings)
  const { showWeightDecimals } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const [customerFilter, setCustomerFilter] = useState({ name: currentCustomer?.displayName, id: currentCustomer?.id })
  const [selectedInboundTicketIds, setSelectedInboundTicketIds] = useState(selectedList)

  const [filter, setFilter] = useState({
    sort: {
      field: SORTABLE_IBT_FIELDS.CUSTOMER_DISPLAY_NAME,
      ascending: true,
    },
    search: '',
    ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
  })

  const ibtStatuses = values(TICKET_STATUSES).filter(
    status =>
      ![
        TICKET_STATUSES.PAID.id,
        TICKET_STATUSES.VOID.id,
        TICKET_STATUSES.VOIDED.id,
        TICKET_STATUSES.AVAILABLE.id,
        TICKET_STATUSES.SHIPPED.id,
      ].includes(status.id),
  )

  const additionalFilterOptions = [
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'statuses',
      label: 'Status',
      options: ibtStatuses,
      width: '150px',
    },
    {
      type: FILTER_OPTION_TYPES.SELECT,
      name: 'preferredPaymentMethod',
      label: 'Pref. Payment Method',
      options: values(ALL_PAYMENT_METHOD_OPTIONS),
      width: '200px',
    },
    {
      type: FILTER_OPTION_TYPES.MULTI_SELECT,
      name: 'paymentTerms',
      label: 'Payment Terms',
      options: paymentTermsOptions,
      width: '150px',
    },
  ]

  /**
   * Effect to update the filter whenever startDate, endDate, or defaultYardTimezone changes.
   */
  useEffect(() => {
    setFilter(prevFilter => ({
      ...prevFilter,
      date: undefined, // clear start date before applying buildFilter
      endDate: undefined, // clear end date before applying buildFilter
      ...JSON.parse(buildFilter(startDate, endDate, defaultYardTimezone)),
    }))
  }, [startDate, endDate, defaultYardTimezone])

  const [paginationOptions, setPaginationOptions] = useState({
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data: inboundTicketList, isFetching } = useGetInboundTicketsQuery(
    {
      filter: JSON.stringify({
        ...filter,
        billQueryParams: {
          customerId: customerFilter?.id || undefined,
          billIds: billIds,
          currency: currentCurrency || undefined,
        },
      }),
      ...paginationOptions,
    },
    { skip: !filter?.sort?.field },
  )

  const transformedRows = (inboundTicketList?.Items || []).slice().map(item => {
    const subTotal = Dinero(item?.netValue)?.toUnit() + (item?.feeOrPromotionTotal || 0)
    const ticketCurrency = item?.netValue?.currency
    const ticketPricePrepend =
      !ticketCurrency || defaultCurrency === ticketCurrency
        ? CURRENCY_PREPENDS[defaultCurrency]?.hc
        : CURRENCY_PREPENDS[ticketCurrency]?.fc

    return {
      id: item.id,
      ticketId: item.ticketId,
      effectiveDate: dateFormatter(item.effectiveDate),
      customerDisplayName: item.customer?.customerCommonIdentifierString,
      customerId: item.customer?.id,
      orderId: item.transportationInfo?.purchaseOrderId,
      orderNumber: item.transportationInfo?.purchaseOrderNumber,
      status: item.status?.value?.replace(/_/g, ' '),
      preferredPaymentMethod: item.customer?.preferredPaymentMethod || '-',
      paymentTerm: item.paymentTerm?.id || '-',
      gross: item.loadWeight?.gross,
      tare: item.loadWeight?.tare,
      netWeight: item.netWeight,
      dueDate: getDueDateFormatted(item.effectiveDate, getPaymentTerms(item)),
      netCost: setDecimalPrecision(subTotal, {
        shouldFormatNumber: false,
        decimalPrecisionOverride: 2,
      }),
      pricePrepend: currencyExchangeRatesFF ? ticketPricePrepend : '$',
    }
  })

  const materialBillColumns = useMemo(
    () => [
      {
        field: 'ticketId',
        headerName: 'Ticket #',
        width: 80,
        editable: false,
        renderCell: params => {
          const { id, ticketId } = params.row
          return (
            <Link to={`/app/purchases/ticket-library/${id}`} target="_blank">
              {ticketId}
            </Link>
          )
        },
      },
      {
        field: 'effectiveDate',
        headerName: 'Date',
        width: 100,
        editable: false,
      },
      {
        field: 'customerDisplayName',
        headerName: 'Customer',
        flex: 1,
        editable: false,
      },
      {
        field: 'orderNumber',
        headerName: 'PO#',
        width: 80,
        editable: false,
        sortable: true,
        valueGetter: params => {
          return Big(params.row?.orderNumber || 0).toNumber()
        },
        renderCell: params => {
          const { orderNumber, orderId } = params.row
          if (!orderNumber || !orderId) return '-'
          return (
            <Link to={`/app/contracts/purchase-orders/${orderId}`} target="_blank">
              {orderNumber}
            </Link>
          )
        },
      },

      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        editable: false,
        sortable: false,
        valueGetter: params => {
          const { status } = params.row
          return startCase(toLower(status))
        },
      },
      {
        field: 'preferredPaymentMethod',
        headerName: 'Preferred Payment Method',
        flex: 1.5,
        editable: false,
        sortable: true,
      },
      {
        field: 'paymentTerm',
        headerName: 'Payment Term',
        flex: 1,
        editable: false,
        sortable: true,
        valueGetter: params => {
          const { paymentTerm } = params.row
          return paymentTerm === '-' ? '-' : getPaymentTermNameById(paymentTerm) || '-'
        },
      },
      {
        field: 'netWeight',
        headerName: 'Net Weight',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { netWeight } = params.row
          return `${setDecimalPrecision(netWeight, {
            removeTrailingZeros: true,
            decimalPrecisionOverride: showWeightDecimals ? 1 : 0,
          })} ${defaultYardUm}`
        },
        editable: false,
      },
      {
        field: 'dueDate',
        headerName: 'Due Date',
        width: 100,
        editable: false,
        sortable: false,
      },
      {
        field: 'netCost',
        headerName: 'Value',
        headerAlign: 'right',
        align: 'right',
        flex: 1,
        valueGetter: params => {
          const { netCost, pricePrepend = '$' } = params.row
          return `${pricePrepend}${setDecimalPrecision(netCost, { decimalPrecisionOverride: 2 })}`
        },
        editable: false,
      },
    ],
    [showWeightDecimals, defaultYardUm, getPaymentTermNameById],
  )

  const handleSortModelChange = useCallback(sortModel => {
    setFilter(prevFilter => ({
      ...prevFilter,
      sort: {
        field: sortModel?.[0]?.field,
        ascending: sortModel?.[0]?.sort === 'asc',
      },
    }))
  }, [])

  // In addition to handling ticket selection, this handleSelectionModelChange function also handles adding "temporary" filters
  // These temporary filters are only relevant if there is no customer selected on the bill before the user opens the modal (eg. currentCustomer)
  // The purpose of these temporary filters is to prevent users from mixing customers in the same bill
  const handleSelectionModelChange = selectionModel => {
    setSelectedInboundTicketIds(selectionModel)

    if (isBatchBill) return

    const ticketWasAdded = selectionModel.length > selectedInboundTicketIds.length

    if (ticketWasAdded) {
      const newId = difference(selectionModel, selectedInboundTicketIds)[0]
      const addedInboundTicket = transformedRows.find(ticket => ticket.id === newId)

      // after a ticket is selected, make sure we only show tickets belonging to that same customer
      if (addedInboundTicket) {
        setCustomerFilter({ name: addedInboundTicket?.customerDisplayName, id: addedInboundTicket?.customerId })
      }
    } else {
      // if all tickets are removed, revert back to currentCustomer (which could be null)
      if (selectionModel.length === 0) {
        setCustomerFilter({ name: currentCustomer?.displayName, id: currentCustomer?.id })
      }
    }
  }

  return (
    <DialogModal
      {...restProps}
      onClose={() => {
        onClose()
      }}
      onSave={() => onAdd(selectedInboundTicketIds)}
      dialogStyles={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: 1400,
          height: '90vh',
        },
      }}
    >
      <Stack sx={{ height: '75vh' }}>
        <DataGridWithSearchV2
          density="compact"
          height="auto"
          rows={transformedRows}
          columns={materialBillColumns}
          apiRef={apiRef}
          useBackendSortAndFilter
          renderDateRangePicker={renderDateRangePicker}
          filter={filter}
          setFilter={setFilter}
          checkboxSelection
          disableColumnMenu
          loading={isFetching}
          showFilterChips
          filterChips={<>{customerFilter?.id && <Chip label={`Customer: ${customerFilter?.name}`} />}</>}
          additionalFilterOptions={additionalFilterOptions}
          initialState={{
            sorting: {
              sortModel: [{ field: materialBillColumns[2].field, sort: 'asc' }],
            },
            selection: {
              selectionModel: selectedList || [],
            },
          }}
          // pagination
          rowCount={inboundTicketList?.totalResultsCount || 0}
          paginationMode="server"
          onPageChange={newPageNumber => setPaginationOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
          onPageSizeChange={newPageSize => setPaginationOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
          autoPageSize={false}
          pageSize={paginationOptions.pageSize}
          // sorting
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          onSortModelChange={handleSortModelChange}
          // selection
          keepNonExistentRowsSelected
          selectionModel={selectedInboundTicketIds}
          onSelectionModelChange={handleSelectionModelChange}
          isRowSelectable={params => {
            if (enableLockAccountingV2) return true // if lock accounting V2 is turned on, we do not need to disable any fields
            if (selectedInboundTicketIds.indexOf(params.id) > -1) return true // allow users to select/unselect tickets that are already linked to the bill
            if (!lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return true
            const inboundTicketIsLocked =
              new Date(params.row?.effectiveDate).getTime() <
              new Date(lockAccountingPeriod.lockAccountingDate).getTime()
            return (
              !inboundTicketIsLocked ||
              params.row?.status === TICKET_STATUSES.PRICE_COMPLETE.id ||
              params.row?.value === TICKET_STATUSES.APPROVED.id
            )
          }}
        />
      </Stack>
    </DialogModal>
  )
}

InboundTicketsModal.propTypes = {
  ...DialogModal.propTypes,
  selectedList: PropTypes.arrayOf(PropTypes.string),
  billIds: PropTypes.arrayOf(PropTypes.string),
  currentCustomer: PropTypes.object,
  getPaymentTerms: PropTypes.func,
  currentCurrency: PropTypes.string,
  isBatchBill: PropTypes.bool,
}

InboundTicketsModal.defaultProps = {
  ...DialogModal.defaultProps,
  title: 'Import Tickets to Payment',
  selectedList: [],
  billIds: [],
  currentCustomer: null,
  getPaymentTerms: noop,
  currentCurrency: null,
  isBatchBill: false,
}

export default InboundTicketsModal
